import { AuthContextProvider } from '@streamelements/client-sdk-js';
import { ServiceEnvironment } from '@streamelements/client-sdk-js';
import { FormValues } from '.';
import {
  createOutputDestinationStreamSettingsProvider,
  DestinationMetadataApiClient,
} from '../../services/DestinationsApiClient';
import { UnauthenticatedError } from '../../utils/auth';
import { platformPrettyName } from '../../constants/common';
import { createAndCaptureError } from '../../utils/createAndCaptureError';
import { runLoginFlow } from '../../api/connectPlatformAccount';
import { CreatorIneligibilityError, TransientError } from '../../types/errors';
import { captureException } from '@sentry/react';

export async function setupStreamRoom({
  authContextProvider,
  serviceEnvironment,
  destinationMetadataApiClient,
  streamData,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  destinationMetadataApiClient: DestinationMetadataApiClient;
  streamData: FormValues['outputs'][string];
}) {
  let retries = 0;
  const {
    title,
    id: outputId,
    category = '',
    privacyStatus,
    broadcastId = 'new',
    youtubeLiveStreamId = 'new',
    xSourceId = 'new',
    xChatAccessOption = '1',
    ...rest
  } = streamData;

  const { provider } = destinationMetadataApiClient.get();

  async function doSetup() {
    const streamRoomProvider = createOutputDestinationStreamSettingsProvider({
      authContextProvider,
      serviceEnvironment,
      destinationMetadata: destinationMetadataApiClient.get(),
    });

    await streamRoomProvider.setupStreamRoom({
      title: title ?? '',
      outputId,
      categoryId: category,
      destinationMetadata: destinationMetadataApiClient.get(),
      privacyStatus,
      broadcastId,
      liveStreamId: youtubeLiveStreamId,
      xSourceId,
      xChatAccessOption,
      ...rest,
    });

    return destinationMetadataApiClient.get();
  }

  while (retries < 4) {
    try {
      const result = await doSetup();
      return result;
    } catch (error) {
      if (error instanceof CreatorIneligibilityError) {
        captureException(error);

        throw error;
      }

      console.warn('Error thrown while setting up stream room: ', (error as Error)?.toString());

      ++retries;

      if (retries === 3) {
        await runLoginFlow({
          authContextProvider,
          serviceEnvironment,
          provider,
        });
        continue;
      }

      if (error instanceof UnauthenticatedError) {
        await destinationMetadataApiClient.refreshTokens();
        continue;
      }

      if (retries < 3) {
        continue;
      }

      throw error;
    }
  }

  throw createAndCaptureError(new TransientError(`Failed to setup your stream on ${platformPrettyName[provider]}. Please try again.`))
    .error;
}

export async function updateStreamRoom({
  authContextProvider,
  serviceEnvironment,
  destinationMetadataApiClient,
  streamData,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  destinationMetadataApiClient: DestinationMetadataApiClient;
  streamData: FormValues['outputs'][string];
}) {
  let retries = 0;
  const { title, id: outputId, category = '', privacyStatus } = streamData;

  while (retries < 3) {
    try {
      const streamRoomProvider = createOutputDestinationStreamSettingsProvider({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata: destinationMetadataApiClient.get(),
      });

      await streamRoomProvider.updateStreamRoom({
        title,
        outputId,
        categoryId: category,
        privacyStatus,
      });

      await destinationMetadataApiClient.updateDestination(
        {
          outputs: destinationMetadataApiClient.get().outputs.map((output) => {
            if (output.id !== outputId) return output;

            const result = output;

            if (title) {
              result.auxiliaryData.streamTitle = title;
            }
            if (category) {
              result.auxiliaryData.streamCategory = category;
            }

            return result;
          }),
        },
        {
          notifyOBS: false,
        }
      );

      return destinationMetadataApiClient.get();
    } catch (error) {
      if (error instanceof UnauthenticatedError) {
        await destinationMetadataApiClient.refreshTokens();
        retries++;
        continue;
      }

      throw error;
    }
  }

  throw new Error('Failed to update stream room');
}
